import React, { useState, useCallback, useEffect } from 'react';
import moment from 'moment';
import debounce from 'lodash/debounce';

import { dueDateTimeFormat } from 'Shared/utils/DateTime.utils';
import { NOON_TIME } from 'Shared/constants/constants';

import WpIcon from 'Wpds/components/icon/WpIcon';
import WpCalendarPicker from 'Wpds/components/calendar-input/WpCalendarPicker';
import {
  WpDropdown,
  WpDropdownToggle,
  WpDropdownMenu,
  WpDropdownMenuItem,
  WpDropdownMenuDivider,
} from 'Wpds/components/dropdown';

import TimeInput from 'Shared/components/TimeInput/TimeInput';

import './DueByDropdown.scss';

type Props = {
  children?: React.ReactNode;
  dueByDate: number;
  handleUpdateDueByDate: Function;
  disabled?: boolean;
};

const DueByDropdown = ({ children, dueByDate, handleUpdateDueByDate, disabled }: Props) => {
  const initialTimeOfDay = dueByDate ? moment.unix(dueByDate).format('HH:mm') : NOON_TIME;

  const [timeOfDay, setTimeOfDay] = useState(initialTimeOfDay);
  const [currentDate, setCurrentDate] = useState(dueByDate);

  const handleUpdateDateTime = useCallback(
    time => {
      handleUpdateDueByDate(time);
    },
    [handleUpdateDueByDate]
  );

  const handleDateChanged = useCallback(
    date => {
      const unixDateTime = dueDateTimeFormat(date, timeOfDay);
      setCurrentDate(unixDateTime);
      handleUpdateDateTime(unixDateTime);
    },
    [timeOfDay, handleUpdateDateTime]
  );

  const handleTomorrowClick = () => {
    const tomorrow = moment(new Date()).add(1, 'days');
    handleDateChanged(tomorrow);
  };

  const handleNextWeekClick = () => {
    const nextWeek = moment(new Date()).add(1, 'week');
    handleDateChanged(nextWeek);
  };

  const handleNoDueDateClick = () => {
    handleDateChanged(null);
  };

  const handleCalendarSelect = date => {
    handleDateChanged(date);
  };

  const debouncedUpdateDateTime = useCallback(debounce(handleUpdateDateTime, 500), [timeOfDay]);

  const handleTimeChange = time => {
    setTimeOfDay(time);
    const date = currentDate ? moment.unix(currentDate) : moment();
    const unixDateTime = dueDateTimeFormat(date, time);
    debouncedUpdateDateTime(unixDateTime);
  };

  useEffect(() => {
    setTimeOfDay(initialTimeOfDay);
  }, [dueByDate, initialTimeOfDay]);

  return (
    <WpDropdown disabled={disabled}>
      <WpDropdownToggle>{children}</WpDropdownToggle>
      <WpDropdownMenu fitHeight>
        <WpDropdownMenuItem onClick={handleTomorrowClick}>
          <WpIcon name="sun" />
          Due tomorrow
        </WpDropdownMenuItem>
        <WpDropdownMenuItem onClick={handleNextWeekClick}>
          <WpIcon name="calendar-alt" />
          Due next week
        </WpDropdownMenuItem>
        <WpDropdownMenuItem onClick={handleNoDueDateClick}>
          <WpIcon name="ban" />
          No due date
        </WpDropdownMenuItem>
        <WpDropdownMenuDivider />
        <li className="DueByDropdown__calendar">
          <WpCalendarPicker
            selectedDay={dueByDate && moment.unix(dueByDate)}
            onDateChange={handleCalendarSelect}
            daySize={30}
          />
        </li>
        <WpDropdownMenuDivider />
        <li className="DueByDropdown__time">
          <p className="DueByDropdown__time__label">Time</p>
          <TimeInput value={timeOfDay} className="form-control" onChange={handleTimeChange} />
        </li>
      </WpDropdownMenu>
    </WpDropdown>
  );
};

export default DueByDropdown;
